import { clientApi } from '@phoenix/clientApi';
import { kakaoTrack } from '@phoenix/helpers/kakao';
import { wishlistLocalStorageHelper } from '@phoenix/helpers/product-localstorage-helper';
import { findIndex, isEmpty } from 'lodash-es';
import * as types from './mutations-types';
import { emitter, PRODUCT_FAVOURITED, PRODUCT_UNFAVOURITED } from '../event-bus';

const initialState = {
  products: [],
  areProductsLoaded: false,
  wishlistFromLocalStorage: [],
  isLoading: false,
  error: null,
};

const getters = {
  hasProducts: (state) => !isEmpty(state.products),
  productsCount: (state) => state.products.length,
  hasItemsInWishlistFromLocalStorage: (state) =>
    state.wishlistFromLocalStorage ? state.wishlistFromLocalStorage.length : false,
  isInWishlist: (state) => (reference) =>
    findIndex(state.wishlistFromLocalStorage, (product) => product.toUpperCase() === reference.toUpperCase()) !== -1,
  itemsInWishlistFromLocalStorageCount: (state) =>
    state.wishlistFromLocalStorage ? state.wishlistFromLocalStorage.length : 0,
};

const actions = {
  async loadWishlistProducts({ commit, dispatch }) {
    try {
      commit(types.WISHLIST_LOAD);

      const { data } = await clientApi.get(window.route('api.customer.wishlist.index'));

      if (data.status) {
        commit(types.WISHLIST_LOAD_SUCCESS, { products: data.data.products });
        wishlistLocalStorageHelper.clear();
        data.data.products.forEach((product) => {
          wishlistLocalStorageHelper.add(product.modelReference);
        });
        dispatch('refreshWishlistFromLocalStorage');
      } else {
        dispatch('showDefaultError');
      }
    } catch (error) {
      window.Phoenix.sentry.captureException(error);
      const errorData = error.response.data;
      commit(types.WISHLIST_ERROR, { error: errorData.message });
    }
  },

  refreshWishlistFromLocalStorage({ commit }) {
    commit(types.WISHLIST_REFRESH_FROM_LOCALSTORAGE, wishlistLocalStorageHelper.get());
  },

  /**
   * Toggle a product in the wishlist
   */
  async toggleProduct({ dispatch, getters }, { reference, internalReference = '', showBanner }) {
    return dispatch(getters.isInWishlist(reference) ? 'removeProduct' : 'addProduct', {
      reference,
      internalReference,
      showBanner,
    });
  },

  async removeProduct({ commit, dispatch }, { reference, internalReference }) {
    try {
      const { data } = await clientApi.delete(window.route('api.customer.wishlist.delete', { reference }));
      if (data.status) {
        commit(types.WISHLIST_REMOVE_REFERENCE_SUCCESS, { reference });
        wishlistLocalStorageHelper.remove(reference);
        dispatch('refreshWishlistFromLocalStorage');

        emitter.emit(PRODUCT_UNFAVOURITED, {
          reference: reference ?? '',
          internalReference: internalReference ?? '',
        });

        return;
      } else {
        dispatch('showDefaultError');
      }
    } catch (error) {
      window.Phoenix.sentry.captureException(error);
      const errorData = error.response.data;
      commit(types.WISHLIST_ERROR, {
        error: errorData.message,
      });
    }
  },

  async addProduct({ commit, dispatch }, { reference, internalReference, showBanner }) {
    try {
      const { data } = await clientApi.post(window.route('api.customer.wishlist.store', { reference }));

      if (data.status) {
        commit(types.WISHLIST_ADD_REFERENCE_SUCCESS, {
          productPage: data.data.page,
        });
        if (showBanner) {
          commit(types.WISHLIST_SHOW_PROMOTION_BLOCK);
        }
        wishlistLocalStorageHelper.add(reference);
        if (window.Phoenix.currentCountry === 'KR') {
          kakaoTrack('addToWishList');
        }
        dispatch('refreshWishlistFromLocalStorage');

        emitter.emit(PRODUCT_FAVOURITED, {
          reference: reference ?? '',
          internalReference: internalReference ?? '',
        });

        return true;
      } else {
        dispatch('showDefaultError');
      }
    } catch (error) {
      window.Phoenix.sentry.captureException(error);
      const errorData = error.response?.data ?? '';
      commit(types.WISHLIST_ERROR, {
        error: errorData.message,
      });
    }
  },

  showDefaultError({ commit }) {
    commit(types.WISHLIST_ERROR, {
      error: window.Phoenix.i18n.t('Form.validations.default'),
    });
  },
};

const mutations = {
  [types.WISHLIST_LOAD](state) {
    state.isLoading = true;
  },

  [types.WISHLIST_LOAD_SUCCESS](state, { products }) {
    state.products = products;
    state.isLoading = false;
    state.areProductsLoaded = true;
  },

  [types.WISHLIST_ERROR](state, { error }) {
    state.error = error;
    state.isLoading = false;
  },

  [types.WISHLIST_REMOVE_REFERENCE_SUCCESS](state, { reference }) {
    state.products = state.products.filter((product) => product.modelReference !== reference);
  },

  [types.WISHLIST_ADD_REFERENCE_SUCCESS](state, { productPage }) {
    state.products.push(productPage);
  },

  [types.WISHLIST_REFRESH_FROM_LOCALSTORAGE](state, value) {
    state.wishlistFromLocalStorage = value;
  },

  [types.WISHLIST_SHOW_PROMOTION_BLOCK](state) {
    const promotionBlock = !state['currentUser/isLogged']
      ? window.Phoenix.promotionWishlistGuest
      : window.Phoenix.promotionWishlist;
    promotionBlock.show();
    setTimeout(() => {
      promotionBlock.hide();
    }, 5000);
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
